import React, { useRef, useState } from 'react';
import { downloadTable } from '../utils/tableExport';
import './FlexibleTable.css';

const elements = (children, prefix = '') => React.Children.toArray(children).flatMap(child =>
  child?.type === React.Fragment ? elements(child.props.children, prefix + child.key) : React.isValidElement(child) ? [React.cloneElement(child, { key: prefix + child.key })] : []);
export function cellValue(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return node;
  if (Array.isArray(node)) return node.map(cellValue).join('');
  const props = node.props || {};
  if (props['data-export-value'] !== undefined) return props['data-export-value'];
  if (props['aria-hidden']) return '';
  if (node.type === 'input') return props.type === 'checkbox' ? Boolean(props.checked) : props.value ?? props.defaultValue ?? '';
  if (node.type === 'select' || node.type === 'textarea') return props.value ?? '';
  if (node.type === 'details') return cellValue(elements(props.children).find(child => child.type === 'summary'));
  const children = React.Children.toArray(props.children);
  return children.length === 1 ? cellValue(children[0]) : children.map(cellValue).join('');
}
export function moveItem(ids, source, target) {
  const result = [...ids], from = result.indexOf(source), to = result.indexOf(target);
  if (from < 0 || to < 0 || from === to) return result;
  result.splice(from, 1); result.splice(to, 0, source); return result;
}
const arranged = (items, order, id) => {
  const ranks = new Map(order.map((key, i) => [key, i]));
  return [...items].sort((a, b) => (ranks.get(id(a)) ?? Infinity) - (ranks.get(id(b)) ?? Infinity));
};
export default function FlexibleTable({ children, exportName = 'table', pageStart = 0, pageSize = Infinity, ...props }) {
  const [editing, setEditing] = useState(false), [columnOrder, setColumnOrder] = useState([]), [rowOrder, setRowOrder] = useState([]);
  const [scope, setScope] = useState('all'), [error, setError] = useState('');
  const drag = useRef(null);
  const sections = elements(children), header = sections.find(section => section.type === 'thead');
  const headerRow = elements(header?.props.children)[0];
  const headers = elements(headerRow?.props.children);
  const columns = headers.map((cell, index) => ({ cell, index, id: String(cell.props['data-column-id'] ?? cell.key ?? index) }));
  const orderedColumns = arranged(columns, columnOrder, col => col.id);
  const body = sections.find(section => section.type === 'tbody');
  const rows = elements(body?.props.children);
  const rowId = row => String(row.key);
  const isData = row => elements(row.props.children).length === headers.length && !elements(row.props.children).some(cell => Number(cell.props.colSpan) > 1);
  const orderedRows = arranged(rows, rowOrder, rowId), pageRows = orderedRows.slice(pageStart, pageStart + pageSize);
  const reorder = (kind, source, target) => {
    if (kind === 'column') setColumnOrder(moveItem(orderedColumns.map(col => col.id), source, target));
    else setRowOrder(moveItem(orderedRows.map(rowId), source, target));
  };
  const drop = (event, kind, target) => {
    if (drag.current?.kind !== kind) return;
    event.preventDefault(); event.stopPropagation(); reorder(kind, drag.current.id, target); drag.current = null;
  };
  const handle = (kind, id, label) => <button type="button" className="table-drag-handle" draggable
    aria-label={`Move ${kind} ${label}`} title={`Drag to reorder; use ${kind === 'column' ? 'Left/Right' : 'Up/Down'} arrow keys to move`}
    onClick={event => event.stopPropagation()} onDoubleClick={event => event.stopPropagation()}
    onDragStart={event => { event.stopPropagation(); drag.current = { kind, id }; event.dataTransfer?.setData('text/plain', id); }}
    onDragEnd={() => { drag.current = null; }} onKeyDown={event => {
      const keys = kind === 'column' ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown'];
      if (!keys.includes(event.key)) return;
      event.preventDefault(); event.stopPropagation();
      const ids = kind === 'column' ? orderedColumns.map(col => col.id) : orderedRows.map(rowId);
      const next = ids.indexOf(id) + (event.key === keys[0] ? -1 : 1);
      if (next >= 0 && next < ids.length) reorder(kind, id, ids[next]);
    }}>↕</button>;
  const exportTable = format => {
    try {
      const selectedRows = (scope === 'page' ? pageRows : orderedRows).filter(isData);
      downloadTable([
        orderedColumns.map(({ cell }) => cell.props['data-export-label'] || cell.props['aria-label'] || String(cellValue(cell)).replace(/\s*[↑↓]\s*$/, '').trim()),
        ...selectedRows.map(row => { const cells = elements(row.props.children); return orderedColumns.map(col => cellValue(cells[col.index])); }),
      ], format, exportName); setError('');
    } catch (err) { setError(`Unable to export table: ${err.message}`); }
  };
  const renderRow = row => {
    if (!isData(row)) return row;
    const cells = elements(row.props.children), id = rowId(row);
    return React.cloneElement(row, { key: id,
      onDragOver: event => { if (drag.current?.kind === 'row') event.preventDefault(); },
      onDrop: event => drop(event, 'row', id),
    }, orderedColumns.map((col, i) => React.cloneElement(cells[col.index], { key: col.id },
      <>{editing && i === 0 && handle('row', id, cellValue(cells[headers.findIndex(h => /^(Unit ID|ID|Cluster)$/i.test(String(h.props['data-export-label'] || cellValue(h)).replace(/[↑↓]/g, '').trim()))] || cells[0]))}{cells[col.index].props.children}</>)));
  };
  return <>
    <div className="table-tools" aria-label={`${exportName} table controls`}>
      <button type="button" onClick={() => exportTable('csv')}>Export CSV</button>
      <button type="button" onClick={() => exportTable('xlsx')}>Export XLSX</button>
      {Number.isFinite(pageSize) && <label>Export rows<select aria-label="Export rows" value={scope} onChange={event => setScope(event.target.value)}><option value="all">All filtered rows</option><option value="page">Current page</option></select></label>}
      <button type="button" aria-pressed={editing} onClick={() => setEditing(value => !value)}>Reorder rows / columns</button>
      {(editing || rowOrder.length > 0 || columnOrder.length > 0) && <button type="button" onClick={() => { setColumnOrder([]); setRowOrder([]); }}>Reset table order</button>}
      {editing && <span>Drag a handle, or focus it and use arrow keys.</span>}
      {error && <span role="alert">{error}</span>}
    </div>
    <table {...props}>{sections.map(section => {
      if (section.type === 'thead') return React.cloneElement(section, { key: 'thead', onClickCapture: event => {
        if (!event.target.closest('.table-drag-handle')) setRowOrder([]);
      } }, React.cloneElement(headerRow, {}, orderedColumns.map(col => React.cloneElement(col.cell, { key: col.id,
        onDragOver: event => { if (drag.current?.kind === 'column') event.preventDefault(); }, onDrop: event => drop(event, 'column', col.id),
      }, <>{editing && handle('column', col.id, cellValue(col.cell))}{col.cell.props.children}</>))));
      if (section.type === 'tbody') return React.cloneElement(section, { key: 'tbody' }, pageRows.map(renderRow));
      return section;
    })}</table>
  </>;
}
